import React, { useEffect, useState } from "react";
import {
  Dialog,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Card,
  CardHeader,
  CardContent,
  Divider,
  Button,
  Table,
  TableContainer,
  Paper,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  CircularProgress,
  Tabs,
  Tab,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { fetchWithToken } from "../../../api/apiutils";
import UserDialogModal from "./UserDialogModal";
import UserActionsModal from "./UserActionsModal";
import SummaryDetailsModal from "./SummaryDetailsModal";
import GetLinksModal from "./GetLinksModal";
import UserSubscription from "./UserSubscription";
import BinCompanyDetailsCard from "./BinCompanyDetailsCard";

// Единый стиль карточек — тень + скругление, чтобы границы всех блоков
// на странице были хорошо видны
const cardSx = {
  borderRadius: 3,
  boxShadow: "0 10px 28px rgba(0,0,0,0.16), 0 3px 8px rgba(0,0,0,0.10)",
  border: "1px solid rgba(0,0,0,0.06)",
};

const getValue = (value) => {
  if (
    value === null ||
    value === undefined ||
    value === "" ||
    value?.length === 0
  ) {
    return "Не указано";
  }
  return value;
};

function formatDateTime(dt) {
  if (!dt) return "Нет данных";
  const d = new Date(dt);
  return d.toLocaleString("ru-RU", {
    dateStyle: "short",
    timeStyle: "short",
    timeZone: "Asia/Almaty",
  });
}

/**
 * Пагинация для табличных витрин статистики (КТРУ, поисковые фразы).
 * Если бэкенд отдаёт "all" — считаем точное число страниц по нему.
 * Если поля "all" нет — определяем наличие следующей страницы косвенно:
 * кнопка "Далее" доступна, пока текущая страница заполнена полностью
 * (данных пришло столько же, сколько PageLimit).
 */
function StatsPagination({
  page,
  onPageChange,
  pageLimit,
  totalCount,
  currentCount,
  loading,
}) {
  const hasAll = typeof totalCount === "number";
  const totalPages = hasAll
    ? Math.max(1, Math.ceil(totalCount / pageLimit))
    : null;
  const canGoBack = page > 1;
  const canGoNext = hasAll
    ? page < totalPages
    : (currentCount || 0) >= pageLimit;

  if (!hasAll && page === 1 && !canGoNext) return null; // одна неполная страница — пагинация не нужна
  if (hasAll && totalPages <= 1) return null;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        gap: 1,
        mt: 1,
      }}
    >
      <Button
        size="small"
        disabled={!canGoBack || loading}
        onClick={() => onPageChange(page - 1)}
      >
        Назад
      </Button>
      <Typography variant="body2" color="text.secondary">
        {hasAll ? `Страница ${page} из ${totalPages}` : `Страница ${page}`}
      </Typography>
      <Button
        size="small"
        disabled={!canGoNext || loading}
        onClick={() => onPageChange(page + 1)}
      >
        Далее
      </Button>
    </Box>
  );
}

const UserPassportModal = ({ open, onClose, item, actionDateFrom }) => {
  const userId = item?.user?.id || item?.userId || null;

  // Модалки, дублирующие функционал старых столбцов таблицы
  const [dialogOpen, setDialogOpen] = useState(false);
  const [userDialogUserId, setUserDialogUserId] = useState(null);
  const [userDialogType, setUserDialogType] = useState(null);

  const [actionsModalOpen, setActionsModalOpen] = useState(false);
  const [actionsUserId, setActionsUserId] = useState(null);
  const [actionsType, setActionsType] = useState(null);

  const [isSummaryModalOpen, setIsSummaryModalOpen] = useState(false);
  const [isGetLinksModalOpen, setIsGetLinksModalOpen] = useState(false);
  const [selectedAnalysisId, setSelectedAnalysisId] = useState(null);

  const [subOpen, setSubOpen] = useState(false);
  const [subUserName, setSubUserName] = useState("");

  // Табы: 0 — КТРУ и поисковые фразы, 1 — карточки компаний по БИН
  const [activeTab, setActiveTab] = useState(0);

  // 9.2 Лайкнутые КТРУ
  const [ktruLoading, setKtruLoading] = useState(false);
  const [ktruData, setKtruData] = useState(null);
  const [ktruError, setKtruError] = useState(null);
  const [ktruPage, setKtruPage] = useState(1);

  // 9.3 Частые поисковые фразы
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchData, setSearchData] = useState(null);
  const [searchError, setSearchError] = useState(null);
  const [searchPage, setSearchPage] = useState(1);

  const STATS_PAGE_LIMIT = 20;

  useEffect(() => {
    if (!open || !userId) return;
    let ignore = false;

    async function loadKtru() {
      setKtruLoading(true);
      setKtruError(null);
      try {
        const res = await fetchWithToken(
          `/api/lots/favourite/byUser/ktru-statistics?UserId=${userId}&Page=${ktruPage}&PageLimit=${STATS_PAGE_LIMIT}`,
        );
        const json = await res.json();
        if (!ignore) setKtruData(json);
      } catch (e) {
        console.error("Ошибка загрузки статистики КТРУ:", e);
        if (!ignore) setKtruError("Не удалось загрузить данные");
      } finally {
        if (!ignore) setKtruLoading(false);
      }
    }

    loadKtru();

    return () => {
      ignore = true;
    };
  }, [open, userId, ktruPage]);

  useEffect(() => {
    if (!open || !userId) return;
    let ignore = false;

    async function loadSearch() {
      setSearchLoading(true);
      setSearchError(null);
      try {
        const res = await fetchWithToken(
          `/api/filters/byUser/search-statistics?UserId=${userId}&Page=${searchPage}&PageLimit=${STATS_PAGE_LIMIT}`,
        );
        const json = await res.json();
        if (!ignore) setSearchData(json);
      } catch (e) {
        console.error("Ошибка загрузки поисковых фраз:", e);
        if (!ignore) setSearchError("Не удалось загрузить данные");
      } finally {
        if (!ignore) setSearchLoading(false);
      }
    }

    loadSearch();

    return () => {
      ignore = true;
    };
  }, [open, userId, searchPage]);

  useEffect(() => {
    if (!open) {
      setKtruData(null);
      setSearchData(null);
      setKtruPage(1);
      setSearchPage(1);
    }
  }, [open]);

  const renderIdentifiers = () => {
    if (!item?.identifiers?.length) return "Не указано";
    return (
      <Box
        component="span"
        sx={{ display: "inline-flex", flexWrap: "wrap", gap: 1 }}
      >
        {item.identifiers.map((id) => (
          <Box
            key={id}
            component="span"
            sx={{
              px: 1,
              py: 0.25,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
              fontSize: 14,
            }}
          >
            {id}
          </Box>
        ))}
      </Box>
    );
  };

  const openActionsModal = (type) => {
    setActionsUserId(userId);
    setActionsType(type);
    setActionsModalOpen(true);
  };

  const handleButtonClick = (type) => {
    setUserDialogUserId(userId);
    setUserDialogType(type);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setUserDialogUserId(null);
    setUserDialogType(null);
  };

  const handleViewResultClick = (id) => {
    setSelectedAnalysisId(id);
    setIsSummaryModalOpen(true);
  };

  const handleGetLinksClick = (id) => {
    setSelectedAnalysisId(id);
    setIsGetLinksModalOpen(true);
  };

  const handleSummaryModalClose = () => {
    setIsSummaryModalOpen(false);
    setSelectedAnalysisId(null);
  };

  const handleGetLinksModalClose = () => {
    setIsGetLinksModalOpen(false);
    setSelectedAnalysisId(null);
  };

  const handleAddSubscription = () => {
    setSubUserName(item?.userName || "Нет данных");
    setSubOpen(true);
  };

  if (!item) return null;

  function ClickableValue({ onClick, children }) {
    return (
      <Box
        component="span"
        onClick={onClick}
        sx={{
          color: "#047bf3ff",
          cursor: "pointer",
          "&:hover": { textDecoration: "underline" },
        }}
      >
        {children}
      </Box>
    );
  }
  return (
    <Dialog
      fullScreen
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          height: "100%",
          maxHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden", // убираем скролл самого Paper диалога
        },
      }}
    >
      <AppBar
        sx={{ position: "relative", flexShrink: 0 }}
        color="default"
        elevation={1}
      >
        <Toolbar>
          <IconButton
            edge="start"
            onClick={onClose}
            aria-label="Назад"
            sx={{ mr: 1 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography sx={{ flex: 1 }} variant="h6">
            Паспорт пользователя {item.userName || "Нет данных"}
          </Typography>
          <Button color="inherit" onClick={onClose}>
            Назад
          </Button>
        </Toolbar>
      </AppBar>
      <Box
        sx={{
          flex: 1,
          minHeight: 0, // критично для корректной работы overflow внутри flex-контейнера
          overflowY: "auto",
          bgcolor: "background.default",
        }}
      >
        {/* --- Основная информация --- */}
        <Card sx={{ margin: "20px", ...cardSx }}>
          <CardHeader title="Основная информация" />
          <CardContent>
            <Divider sx={{ my: 1 }} />
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                columnGap: 4,
                rowGap: 0.75,
                "& .MuiTypography-root": { fontSize: 13.5 },
              }}
            >
              <Typography>
                <strong>Номер телефона:</strong>{" "}
                <Box
                  component="span"
                  onClick={() => {
                    if (window.confirm("Добавить подписку?")) {
                      handleAddSubscription();
                    }
                  }}
                  sx={{ color: "#047bf3ff", cursor: "pointer" }}
                >
                  {getValue(item.userName)}
                </Box>
              </Typography>
              <Typography>
                <strong>ОС:</strong> {getValue(item.operatingSystem)}
              </Typography>
              <Typography>
                <strong>Дата регистрации:</strong>{" "}
                {formatDateTime(item.dateOfRegistration)}
              </Typography>
              <Typography>
                <strong>Дата последней активности:</strong>{" "}
                {formatDateTime(item.dateOfLastAction)}
              </Typography>
              <Typography>
                <strong>Подписка:</strong> {getValue(item.subscription)}
              </Typography>
              <Typography>
                <strong>Срок подписки:</strong>{" "}
                {item.subscriptionDate?.start && item.subscriptionDate?.end
                  ? `${new Date(item.subscriptionDate.start).toLocaleDateString()} - ${new Date(
                      item.subscriptionDate.end,
                    ).toLocaleDateString()}`
                  : "Нет данных"}
              </Typography>
              <Typography component="div" sx={{ gridColumn: { sm: "1 / -1" } }}>
                <strong>БИН/ИИН:</strong> {renderIdentifiers()}
              </Typography>
            </Box>

            {/* --- Показатели активности (объединены с основной информацией) --- */}
            <Divider sx={{ my: 2 }}>
              <strong>Показатели активности</strong>
            </Divider>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                columnGap: 4,
                rowGap: 0.75,
                "& .MuiTypography-root": { fontSize: 13.5 },
              }}
            >
              <Typography component="div">
                <strong>Количество лайков:</strong>{" "}
                <ClickableValue onClick={() => handleButtonClick("likes")}>
                  {item.countOfLikes || 0}
                </ClickableValue>
              </Typography>
              <Typography component="div">
                <strong>Дизлайк:</strong>{" "}
                <ClickableValue onClick={() => openActionsModal("Dislike")}>
                  {item.countOfDislikes || 0}
                </ClickableValue>
              </Typography>
              <Typography component="div">
                <strong>Мои лайки:</strong>{" "}
                <ClickableValue onClick={() => openActionsModal("Favourite")}>
                  {item.countOfFavourites || 0}
                </ClickableValue>
              </Typography>
              <Typography component="div">
                <strong>Результат ТС:</strong>{" "}
                <ClickableValue onClick={() => openActionsModal("FileData")}>
                  {item.countOfTSData || 0}
                </ClickableValue>
              </Typography>
              <Typography component="div">
                <strong>Анализ:</strong>{" "}
                <ClickableValue onClick={() => handleButtonClick("analysis")}>
                  {item.countOfAnalysis || 0}
                </ClickableValue>
              </Typography>
              <Typography component="div">
                <strong>Подборки:</strong>{" "}
                <ClickableValue onClick={() => handleButtonClick("selection")}>
                  {item.countOfSelection || 0}
                </ClickableValue>
              </Typography>
              <Typography>
                <strong>Сегмент:</strong> {getValue(item.segmentCode)}
              </Typography>
              <Typography component="div">
                <strong>Статистика платных фильтров:</strong>{" "}
                <ClickableValue onClick={() => handleButtonClick("paidfilter")}>
                  {item.countOfPaidFilters || 0}
                </ClickableValue>
              </Typography>
              <Typography>
                <strong>Выгрузка эксель:</strong> {item.countOfExports || 0}
              </Typography>
              <Typography component="div">
                <strong>Поиск:</strong>{" "}
                <ClickableValue
                  onClick={() => handleButtonClick("textFilters")}
                >
                  {item.countOfTextFilters || 0}
                </ClickableValue>
              </Typography>
              <Typography component="div">
                <strong>Активность:</strong>{" "}
                <ClickableValue onClick={() => openActionsModal(null)}>
                  {getValue(item.activityLevelName)}
                </ClickableValue>
              </Typography>
            </Box>
          </CardContent>
        </Card>

        {/* --- Табы: КТРУ/поиск и карточки компаний по БИН --- */}
        <Card sx={{ margin: "20px", ...cardSx }}>
          <Tabs
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue)}
            sx={{ borderBottom: 1, borderColor: "divider", px: 2 }}
          >
            <Tab label="КТРУ и поисковые запросы" />
            <Tab label="Компании по БИН" />
          </Tabs>

          <CardContent>
            {/* --- Таб 1: Лайкнутые КТРУ + Частые поисковые фразы --- */}
            {activeTab === 0 && (
              <Box sx={{ display: "grid", gap: 3 }}>
                <Box>
                  <Divider sx={{ mb: 1 }}>
                    <strong>Лайкнутые КТРУ</strong>
                  </Divider>
                  {ktruLoading ? (
                    <CircularProgress size={24} />
                  ) : ktruError ? (
                    <Typography color="error">{ktruError}</Typography>
                  ) : ktruData && !ktruData.isCalculated ? (
                    <Typography color="text.secondary">
                      Данные ещё подсчитываются
                    </Typography>
                  ) : (
                    <>
                      {ktruData?.unmappedLikedLotsCount > 0 && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 1 }}
                        >
                          Лотов с лайком без сопоставленного КТРУ:{" "}
                          {ktruData.unmappedLikedLotsCount}
                        </Typography>
                      )}
                      <TableContainer
                        component={Paper}
                        variant="outlined"
                        sx={{ boxShadow: "0 6px 16px rgba(0,0,0,0.10)" }}
                      >
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Код КТРУ</TableCell>
                              <TableCell>Название</TableCell>
                              <TableCell>Лайкнуто лотов</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {ktruData?.data?.length ? (
                              ktruData.data.map((row, idx) => (
                                <TableRow key={`${row.truCode}-${idx}`}>
                                  <TableCell>{row.truCode}</TableCell>
                                  <TableCell>
                                    {row.truName || "Название не найдено"}
                                  </TableCell>
                                  <TableCell>{row.count}</TableCell>
                                </TableRow>
                              ))
                            ) : (
                              <TableRow>
                                <TableCell colSpan={3} align="center">
                                  Нет данных
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      <StatsPagination
                        page={ktruPage}
                        onPageChange={setKtruPage}
                        pageLimit={STATS_PAGE_LIMIT}
                        totalCount={ktruData?.all}
                        currentCount={ktruData?.data?.length}
                        loading={ktruLoading}
                      />
                    </>
                  )}
                </Box>

                <Box>
                  <Divider sx={{ mb: 1 }}>
                    <strong>Частые поисковые фразы</strong>
                  </Divider>
                  {searchLoading ? (
                    <CircularProgress size={24} />
                  ) : searchError ? (
                    <Typography color="error">{searchError}</Typography>
                  ) : searchData && !searchData.isCalculated ? (
                    <Typography color="text.secondary">
                      Данные ещё подсчитываются
                    </Typography>
                  ) : (
                    <>
                      <TableContainer
                        component={Paper}
                        variant="outlined"
                        sx={{ boxShadow: "0 6px 16px rgba(0,0,0,0.10)" }}
                      >
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Поисковая фраза</TableCell>
                              <TableCell>Начальных поисков</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {searchData?.data?.length ? (
                              searchData.data.map((row, idx) => (
                                <TableRow key={`${row.query}-${idx}`}>
                                  <TableCell>{row.query}</TableCell>
                                  <TableCell>{row.count}</TableCell>
                                </TableRow>
                              ))
                            ) : (
                              <TableRow>
                                <TableCell colSpan={2} align="center">
                                  Нет данных
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      <StatsPagination
                        page={searchPage}
                        onPageChange={setSearchPage}
                        pageLimit={STATS_PAGE_LIMIT}
                        totalCount={searchData?.all}
                        currentCount={searchData?.data?.length}
                        loading={searchLoading}
                      />
                    </>
                  )}
                </Box>
              </Box>
            )}

            {/* --- Таб 2: карточки компаний по БИН/ИИН пользователя ---
                Раньше по клику на БИН происходил переход на отдельную страницу
                (BinActiveUserPage). Теперь та же логика (поиск/создание анализа,
                поллинг статуса, подгрузка данных) вынесена в компонент
                BinCompanyDetailsCard и подключается прямо в паспорте.
                Карточки идут в ряд и компактны, полная информация раскрывается
                по кнопке «Смотреть подробнее» внутри каждой карточки. */}
            {activeTab === 1 && (
              <>
                {item?.identifiers?.length > 0 ? (
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 2,
                    }}
                  >
                    {item.identifiers.map((bin) => (
                      <BinCompanyDetailsCard key={bin} bin={bin} />
                    ))}
                  </Box>
                ) : (
                  <Typography color="text.secondary">
                    БИН/ИИН у пользователя не указаны
                  </Typography>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </Box>

      {/* Дочерние модалки — тот же функционал, что был у столбцов таблицы */}
      <UserSubscription
        open={subOpen}
        onClose={() => setSubOpen(false)}
        userName={subUserName}
      />

      <UserDialogModal
        open={dialogOpen}
        onClose={handleDialogClose}
        userId={userDialogUserId}
        type={userDialogType}
        onViewResultClick={handleViewResultClick}
        onGetLinksClick={handleGetLinksClick}
        actionDateFrom={actionDateFrom}
      />

      <UserActionsModal
        open={actionsModalOpen}
        onClose={() => setActionsModalOpen(false)}
        userId={actionsUserId}
        actionDateFrom={actionDateFrom}
        actionType={actionsType}
      />

      <SummaryDetailsModal
        open={isSummaryModalOpen}
        onClose={handleSummaryModalClose}
        analysisId={selectedAnalysisId}
      />
      <GetLinksModal
        open={isGetLinksModalOpen}
        onClose={handleGetLinksModalClose}
        analysisId={selectedAnalysisId}
      />
    </Dialog>
  );
};

export default UserPassportModal;
